import * as React from "react";
import ChipInputField from "../../../../components/commonComponents/TextInputs/ChipInputField";

// Normalizes an email string for consistent comparison, stripping "New:" and trimming whitespace.
const normalizeEmailValue = (email) =>
    String(email || "")
        .replace(/^New:\s*/i, "")
        .replace(/:\s*\(Already Shared\)$/i, "")
        .trim()
        .toLowerCase();

// Splits comma-separated input into individual email values.
const parseEmailInput = (input) =>
    String(input || "")
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email !== "");

// Email-only wrapper around ChipInputField. Favorites, "New:", and "Already Shared"
// stay here so other screens can reuse the chip field without this behavior.
const EmailRecipientSelector = ({
    value,
    options,
    sharedEmails,
    onChange,
    label,
    fieldError,
    inputId,
    inputName,
    noOptionsText = "No matches found",
}) => {
    const selectedValues = React.useMemo(
        () => (Array.isArray(value) ? value : []),
        [value]
    );

    // Shared emails stay out of the favorite list, but typed text can still match them.
    const availableOptions = React.useMemo(
        () => (Array.isArray(options) ? options : []),
        [options]
    );

    const sharedEmailValues = React.useMemo(
        () => (Array.isArray(sharedEmails) ? sharedEmails : []),
        [sharedEmails]
    );

    // Rebuilds the suggestion list from whatever is currently typed.
    const getSuggestions = React.useCallback((inputValue) => {
        const normalizedInputValue = inputValue.trim();
        const parsedInputValues = parseEmailInput(inputValue);
        const selectedEmailValues = selectedValues.map(normalizeEmailValue);

        let filtered = availableOptions.filter((option) =>
            option.toLowerCase().indexOf(normalizedInputValue.toLowerCase()) !== -1
        );

        const isExisting = availableOptions.some(
            (option) => option.trim().toLowerCase() === normalizedInputValue.toLowerCase()
        );

        // A typed address that is not already a favorite becomes "New:" or "Already Shared".
        if (normalizedInputValue !== "" && parsedInputValues.length > 0 && !isExisting) {
            const isAlreadyShared = parsedInputValues.some((inputEmail) =>
                sharedEmailValues.some(
                    (email) => normalizeEmailValue(email) === normalizeEmailValue(inputEmail)
                )
            );

            if (isAlreadyShared) {
                filtered = [`${normalizedInputValue}: (Already Shared)`, ...filtered];
            } else {
                filtered = [`New: ${parsedInputValues.join(", ")}`, ...filtered];
            }
        }

        return filtered.filter((option) => !selectedEmailValues.includes(normalizeEmailValue(option)));
    }, [availableOptions, selectedValues, sharedEmailValues]);

    // "New:" stores the raw addresses. "Already Shared" is passed through so the
    // parent can reject it and show the existing toast.
    const resolveOption = React.useCallback((option) => {
        if (/^New:\s*/i.test(option)) {
            return {
                values: parseEmailInput(option.replace(/^New:\s*/i, "")),
                action: "createOption",
            };
        }
        return { values: [option], action: "selectOption" };
    }, []);

    const isSuggestionMuted = React.useCallback(
        (option) => /:\s*\(Already Shared\)$/i.test(String(option)),
        []
    );

    return (
        <ChipInputField
            value={selectedValues}
            options={getSuggestions}
            onChange={onChange}
            label={label}
            fieldError={fieldError}
            inputId={inputId}
            inputName={inputName || "email_recipient"}
            noOptionsText={noOptionsText}
            resolveOption={resolveOption}
            isSuggestionMuted={isSuggestionMuted}
            parseInput={parseEmailInput}
        />
    );
};

export default EmailRecipientSelector;
